import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { fetchStaffJournal } from './staffApi';
import { StaffJournalPage, StaffJournalRow, StaffMember, StaffTarget } from './types';
import { splitMoment } from './staffUtils';

interface StaffJournalPanelProps {
  target: StaffTarget;
  member: StaffMember;
}

const PAGE_SIZES = [10, 25, 50];

const RESULT_STYLE: Record<string, { color: string; bg: string; border: string }> = {
  success: { color: '#047857', bg: '#ecfdf5', border: '#a7f3d0' },
  error: { color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
  denied: { color: '#9a3412', bg: '#fff7ed', border: '#fed7aa' }
};

const headCellSx = { fontWeight: 700, color: '#475569', fontSize: '0.78rem', bgcolor: '#f8fafc', whiteSpace: 'nowrap' as const };

// Карточка сотрудника уже окна: в узкой карточке строки журнала складываются, как в «Моей активности» профиля
const STACKED = '@container (max-width: 639px)';

const stackedRowSx = {
  '& td': { borderColor: '#f1f5f9', verticalAlign: 'top', py: 1.25 },
  [STACKED]: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateAreas: '"when result" "action action" "who who" "section section"',
    columnGap: 2,
    rowGap: 0.5,
    px: 2,
    py: 1.5,
    borderBottom: '1px solid #f1f5f9',
    '&:last-of-type': { borderBottom: 'none' },
    '& td': { border: 'none', p: 0, minWidth: 0, whiteSpace: 'normal' }
  }
} as const;

const inlineCellSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 } as const;

const objectOf = (row: StaffJournalRow): string =>
  [row.entityLabel, row.pharmacyNum ? `аптека ${row.pharmacyNum}` : '', row.period].filter(Boolean).join(' · ');

/** Журнал: действия сотрудника и действия над ним (создание, права, пароль, статус), новые сверху */
const StaffJournalPanel: React.FC<StaffJournalPanelProps> = ({ target, member }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [data, setData] = useState<StaffJournalPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchStaffJournal(target, member.id, page, pageSize));
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить журнал'));
    } finally {
      setLoading(false);
    }
  }, [target, member.id, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const rows = data ? data.rows : [];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: '16px',
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        containerType: 'inline-size'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>Журнал действий</Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>Действия сотрудника и изменения его учётной записи</Typography>
        </Box>
        <Tooltip title="Обновить" arrow>
          <span>
            <IconButton
              size="small"
              onClick={load}
              disabled={loading}
              aria-label="Обновить журнал"
              sx={{ color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', width: 34, height: 34 }}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : <Refresh fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {loadError && (
        <Alert
          severity="error"
          sx={{ borderRadius: '12px' }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Повторить
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {loading && !data && !loadError ? (
        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : data && rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#94a3b8', py: 2 }}>В журнале пока нет записей</Typography>
      ) : data ? (
        <Box>
          <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            <Table size="small" sx={{ [STACKED]: { display: 'block', '& tbody': { display: 'block' } } }}>
              <TableHead sx={{ [STACKED]: { display: 'none' } }}>
                <TableRow>
                  <TableCell sx={headCellSx}>Когда</TableCell>
                  <TableCell sx={headCellSx}>Кто</TableCell>
                  <TableCell sx={headCellSx}>Раздел</TableCell>
                  <TableCell sx={headCellSx}>Действие</TableCell>
                  <TableCell sx={headCellSx}>Результат</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const moment = splitMoment(row.createdAt);
                  const object = objectOf(row);
                  const resultStyle = RESULT_STYLE[row.result] || { color: '#475569', bg: '#f8fafc', border: '#e2e8f0' };
                  const note = row.result === 'success' ? row.summary : row.message || row.summary;
                  const self = row.userId === member.id;
                  return (
                    <TableRow key={row.id} hover sx={stackedRowSx}>
                      <TableCell sx={{ whiteSpace: 'nowrap', [STACKED]: { ...inlineCellSx, gridArea: 'when' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.84rem' }}>{moment.time}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>{moment.date}</Typography>
                      </TableCell>
                      <TableCell sx={{ [STACKED]: { ...inlineCellSx, gridArea: 'who' } }}>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.84rem' }}>
                          {self ? 'Сотрудник' : (row.fullName || row.username || '-')}
                        </Typography>
                        {row.actorUsername && (
                          <Typography variant="caption" sx={{ color: '#9a3412' }}>Вход администратора: {row.actorUsername}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ [STACKED]: { ...inlineCellSx, gridArea: 'section' } }}>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.84rem' }}>{row.moduleTitle || '-'}</Typography>
                        {row.sectionTitle && <Typography variant="caption" sx={{ color: '#64748b' }}>{row.sectionTitle}</Typography>}
                      </TableCell>
                      <TableCell sx={{ minWidth: 200, [STACKED]: { gridArea: 'action' } }}>
                        <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.84rem', fontWeight: 600 }}>{row.actionTitle || '-'}</Typography>
                        {object && <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>{object}</Typography>}
                        {note && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{note}</Typography>}
                      </TableCell>
                      <TableCell sx={{ [STACKED]: { gridArea: 'result' } }}>
                        <Chip
                          label={row.resultTitle || row.result}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            borderRadius: '6px',
                            color: resultStyle.color,
                            bgcolor: resultStyle.bg,
                            border: `1px solid ${resultStyle.border}`
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={data.total}
            page={Math.max(0, data.page - 1)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_, next) => setPage(next + 1)}
            onRowsPerPageChange={e => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(1);
            }}
            labelRowsPerPage="Строк на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
            sx={{
              '& .MuiTablePagination-toolbar': { px: 0, flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 },
              '& .MuiTablePagination-spacer': { display: 'none' }
            }}
          />
        </Box>
      ) : null}
    </Paper>
  );
};

export default StaffJournalPanel;
